import React from 'react';
import './assets/AllTabsCommon.css';

const AREA_WEIGHTS = [
  { area: 'PDN connectivity', weight: '18.00%' },
  { area: 'Registration issues', weight: '18.00%' },
  { area: 'Data stalls', weight: '15.00%' },
  { area: 'Crash stability', weight: '14.00%' },
  { area: 'Modem SSR (crashes)', weight: '8.80%' },
  { area: 'Throughput degradation', weight: '6.00%' },
  { area: 'Thermal issues', weight: '5.20%' },
  { area: 'Memory pressure', weight: '4.00%' },
  { area: 'High CPU usage', weight: '3.20%' },
  { area: 'Signal issues', weight: '3.00%' },
  { area: 'Boot issues', weight: '2.40%' },
  { area: 'Flash storage issues', weight: '2.40%' },
];

const METRIC_THRESHOLDS = [
  { area: 'Data stalls', metric: 'Stall count', good: '≤ 0.5', medium: '> 0.5 to ≤ 1.5', bad: '> 1.5', notes: 'Count; effectively 0, 1, and 2+' },
  { area: 'Signal issues', metric: 'RSRP', good: '≥ −80', medium: '≥ −100 to < −80', bad: '< −100', notes: 'dBm' },
  { area: 'Signal issues', metric: 'RSRQ', good: '≥ −10', medium: '≥ −15 to < −10', bad: '< −15', notes: 'dB' },
  { area: 'Signal issues', metric: 'SINR', good: '≥ 13', medium: '≥ 0 to < 13', bad: '< 0', notes: 'dB' },
  { area: 'Signal issues', metric: 'Signal anomaly count', good: '≤ 0.5', medium: '> 0.5 to ≤ 3.5', bad: '> 3.5', notes: 'Count' },
  { area: 'Registration issues', metric: 'Registration failure rate', good: '≤ 15%', medium: '> 15% to ≤ 30%', bad: '> 30%', notes: 'Percent' },
  { area: 'Registration issues', metric: 'Service-request failure rate', good: '≤ 10%', medium: '> 10% to ≤ 25%', bad: '> 25%', notes: 'Percent' },
  { area: 'PDN connectivity', metric: 'PDN failure rate', good: '≤ 20%', medium: '> 20% to ≤ 35%', bad: '> 35%', notes: 'Percent' },
  { area: 'Throughput degradation', metric: 'Download throughput', good: '≥ 1.0', medium: '≥ 0.8 to < 1.0', bad: '< 0.8', notes: 'Observed Mbps ÷ DL baseline' },
  { area: 'Throughput degradation', metric: 'Upload throughput', good: '≥ 1.0', medium: '≥ 0.8 to < 1.0', bad: '< 0.8', notes: 'Observed Mbps ÷ UL baseline' },
  { area: 'Throughput degradation', metric: 'Throughput variation', good: '≤ 20%', medium: '> 20% to ≤ 40%', bad: '> 40%', notes: 'Coefficient of variation' },
  { area: 'High CPU usage', metric: 'CPU busy', good: '≤ 30%', medium: '> 30% to ≤ 75%', bad: '> 75%', notes: 'Percent' },
  { area: 'Memory pressure', metric: 'RAM used', good: '≤ 1500', medium: '> 1500 to ≤ 1930', bad: '> 1930', notes: 'MB' },
  { area: 'Flash storage issues', metric: 'Flash used', good: '≤ 2000', medium: '> 2000 to ≤ 2400', bad: '> 2400', notes: 'MB' },
  { area: 'Thermal issues', metric: 'Thermal-zone temperature', good: '≤ 70', medium: '> 70 to ≤ 80', bad: '> 80', notes: '°C; readings outside −20 to 120°C are ignored as sensor faults' },
  { area: 'Crash stability', metric: 'Received crash uploads', good: '0', medium: '1', bad: '2+', notes: 'Count per 6-hour scoring window; analysis completion is not required' },
  { area: 'Modem SSR (crashes)', metric: 'Modem SSR count', good: '≤ 0.5', medium: '> 0.5 to ≤ 1.5', bad: '> 1.5', notes: 'Count' },
  { area: 'Boot issues', metric: 'Average boot time', good: '≤ 5', medium: '> 5 to ≤ 8', bad: '> 8', notes: 'Seconds' },
  { area: 'Boot issues', metric: 'Boot-time variation', good: '≤ 2', medium: '> 2 to ≤ 5', bad: '> 5', notes: 'Seconds' },
];

const GLOSSARY = [
  { term: 'RSRP', definition: 'Reference Signal Received Power measures serving-cell signal strength. Higher (less negative) values are stronger.' },
  { term: 'RSRQ', definition: 'Reference Signal Received Quality measures signal quality and the effect of interference. Higher (less negative) values are better.' },
  { term: 'SINR', definition: 'Signal-to-Interference-plus-Noise Ratio indicates how clearly a device receives the signal. Higher values are better.' },
  { term: 'PDN', definition: 'Packet Data Network connectivity: the device’s ability to establish and retain a data connection.' },
  { term: 'Modem SSR', definition: 'A modem subsystem restart, which can interrupt connectivity and service.' },
  { term: 'Stability', definition: 'Device stability based on crash uploads received by the server in each six-hour scoring window, whether or not crash analysis has completed.' },
  { term: 'Telemetry', definition: 'Performance data uploaded by devices for health scoring and fleet monitoring.' },
];

function FAQModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content faq-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fleet-health-guide-title"
      >
        <div className="modal-header">
          <h2 id="fleet-health-guide-title">Fleet Health Guide</h2>
          <button className="modal-close-button" onClick={onClose} title="Close" aria-label="Close Fleet Health Guide">×</button>
        </div>

        <div className="modal-body faq-modal-body">
          <p className="faq-intro">Use this guide to interpret Fleet Health scores, problematic areas, and the threshold bands behind them.</p>

          <section className="faq-section" aria-labelledby="score-severity-heading">
            <h3 id="score-severity-heading">Health Score and Severity</h3>
            <div className="faq-table-wrap">
              <table className="details-table">
                <thead><tr><th>Status</th><th>Score range</th><th>Meaning</th></tr></thead>
                <tbody>
                  <tr><td><strong className="faq-status-stable">Stable</strong></td><td>75–100</td><td>Health is operating within the expected range.</td></tr>
                  <tr><td><strong className="faq-status-degraded">Degraded</strong></td><td>50–74</td><td>Health needs attention; one or more areas are underperforming.</td></tr>
                  <tr><td><strong className="faq-status-critical">Critical</strong></td><td>Below 50</td><td>Health requires prompt investigation.</td></tr>
                </tbody>
              </table>
            </div>
            <p className="faq-note"><strong>Problematic-area severity:</strong> an area is Critical when at least one contributing device is Critical; otherwise it is Degraded. Only areas with a non-stable fleet lookback average are shown.</p>
          </section>

          <section className="faq-section" aria-labelledby="priority-heading">
            <h3 id="priority-heading">Priority</h3>
            <p>Priority is a static investigation ranking, not a measure of current severity or affected-device count. High Priority contains the four highest configured area components; Low Priority contains the remaining areas. When components have equal weights, a stable configuration order selects the four High Priority areas.</p>
          </section>

          <section className="faq-section" aria-labelledby="area-weights-heading">
            <h3 id="area-weights-heading">Area Weights</h3>
            <p>Each final area weight is calculated internally from a category allocation multiplied by that area’s allocation. Only the final contribution is shown here.</p>
            <div className="faq-table-wrap faq-area-weight-wrap">
              <table className="details-table">
                <thead><tr><th>Area</th><th>Final area weight</th></tr></thead>
                <tbody>{AREA_WEIGHTS.map(({ area, weight }) => <tr key={area}><td>{area}</td><td><strong>{weight}</strong></td></tr>)}</tbody>
              </table>
            </div>
          </section>

          <section className="faq-section" aria-labelledby="thresholds-heading">
            <h3 id="thresholds-heading">Metric Thresholds</h3>
            <p>Good, Medium, and Bad bands determine the metric penalties that contribute to an area’s health. Throughput is normalized against its configured baseline; the default baselines are 10 Mbps download and 1 Mbps upload.</p>
            <div className="faq-table-wrap">
              <table className="details-table faq-threshold-table">
                <thead><tr><th>Area</th><th>Metric</th><th>Good</th><th>Medium</th><th>Bad</th><th>Units / notes</th></tr></thead>
                <tbody>
                  {METRIC_THRESHOLDS.map((threshold) => (
                    <tr key={`${threshold.area}-${threshold.metric}`} data-testid="threshold-row">
                      <td>{threshold.area}</td><td>{threshold.metric}</td><td>{threshold.good}</td><td>{threshold.medium}</td><td>{threshold.bad}</td><td>{threshold.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="faq-section faq-section-last" aria-labelledby="glossary-heading">
            <h3 id="glossary-heading">Terms</h3>
            {GLOSSARY.map(({ term, definition }) => (
              <div key={term} className="session-info-item faq-glossary-item">
                <span className="info-label">{term}</span>
                <span className="info-value">{definition}</span>
              </div>
            ))}
          </section>
        </div>

        <div className="faq-modal-footer">
          <button className="submit-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default FAQModal;
